import React, { useState, useEffect } from 'react';
import { CSSProperties } from 'react';
import styled from '@emotion/styled';
import Loading from 'ui/elements/Loading';
import DownloadableFile from './DownloadableFile';
import DownloadIcon from 'ui/elements/icons/DownloadIcon';
import IconButton from 'ui/elements/icons/IconButton';
import ImageLightbox from './ImageLightbox';
import { halfSpacing } from 'ui/theme/themeConstants';
import bluePlanetTheme from 'ui/theme/themeBluePlanet';
import conversationApi from 'apis/CompanyAPI/conversations/conversationApi';

interface ImagePreviewProps {
  fileId?: string;
  filename: string;
  onDownload?: () => Promise<void>;
  localFile?: File;
  localImageUrl?: string;
  maxWidth?: number;
  maxHeight?: number;
  className?: string;
  style?: CSSProperties;
}

const PreviewContainer = styled.div`
  position: relative;
  border-radius: ${bluePlanetTheme.shape.borderRadius * 2}px;
  overflow: hidden;
  cursor: pointer;
  background-color: ${bluePlanetTheme.bluePlanetPalette.grey.veryLight};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const DownloadButtonContainer = styled.div`
  position: absolute;
  top: ${halfSpacing};
  right: ${halfSpacing};
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;

  ${PreviewContainer}:hover & {
    opacity: 0.9;
  }

  /* Always visible on touch screens */
  @media (hover: none) and (pointer: coarse) {
    opacity: 1;
  }
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
  display: block;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 200px;
  background-color: ${bluePlanetTheme.bluePlanetPalette.grey.veryLight};
`;

export default function ImagePreview(props: ImagePreviewProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  // Handle local file or fetch server image
  useEffect(() => {
    let isMounted = true;

    // Handle local file
    if (props.localFile) {
      const url = URL.createObjectURL(props.localFile);
      if (isMounted) {
        setImageUrl(url);
        setIsLoading(false);
      }
      return () => {
        URL.revokeObjectURL(url);
        isMounted = false;
      };
    }

    // Handle local image URL
    if (props.localImageUrl) {
      if (isMounted) {
        setImageUrl(props.localImageUrl);
        setIsLoading(false);
      }
      return () => {
        isMounted = false;
      };
    }

    // Handle server image (existing behavior)
    if (props.fileId) {
      const fetchImage = async () => {
        try {
          const response = await conversationApi.downloadAttachment(props.fileId!);
          if (isMounted) {
            setImageUrl(response.path);
            setIsLoading(false);
          }
        } catch (error) {
          if (isMounted) {
            setHasError(true);
            setIsLoading(false);
          }
        }
      };

      fetchImage();
    }

    return () => {
      isMounted = false;
    };
  }, [props.fileId, props.localFile, props.localImageUrl]);

  // Fallback to DownloadableFile if there's an error or not an image
  if (hasError) {
    return (
      <DownloadableFile
        name={props.filename}
        onDownload={props.onDownload || (() => Promise.resolve())}
        className={props.className}
        style={props.style}
        hasBorder
        backgroundColor="white"
      />
    );
  }

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent lightbox from opening
    props.onDownload?.();
  };

  const handleImageClick = () => {
    setIsLightboxOpen(true); // Open lightbox instead of download
  };

  return (
    <>
      <PreviewContainer
        className={props.className}
        style={{
          maxWidth: props.maxWidth || 300,
          height: props.maxHeight || 200,
          ...props.style,
        }}
        onClick={handleImageClick}
      >
        {isLoading && (
          <LoadingContainer>
            <Loading size={24} />
          </LoadingContainer>
        )}

        {imageUrl && (
          <>
            <PreviewImage
              src={imageUrl}
              alt={props.filename}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{
                display: isLoading ? 'none' : 'block',
              }}
            />
            {props.onDownload && (
              <DownloadButtonContainer>
                <IconButton
                  onClick={handleDownload}
                  color="indigo"
                  hoverColor="blue"
                  backgroundColor="white"
                  title="Download"
                  size="medium"
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </DownloadButtonContainer>
            )}
          </>
        )}
      </PreviewContainer>

      {/* Lightbox Dialog */}
      {isLightboxOpen && imageUrl && (
        <ImageLightbox
          imageUrl={imageUrl}
          filename={props.filename}
          onClose={() => setIsLightboxOpen(false)}
          onDownload={props.onDownload || (() => Promise.resolve())}
        />
      )}
    </>
  );
}
